/* eslint-disable s4l/require-display-name */
import { Checkbox, Separator, Typography } from '@s4l/ds'
import React from 'react'
import { usePriceDetailsContext, getBattlefaceLabel } from '../context'
import { usePriceDetailsHelpers } from '../hooks'
import PaymentDisplay from '../PaymentDisplay'
import PaymentRow from '../PaymentRow'
import RoomSection from '../RoomSection'
import styles from '../styles.module.css'
import { PAYMENT_DISPLAY_TYPES } from '../types'

export default function PriceDetailsContent() {
  const { formatCurrency, formatMessage, formatMessageCapitalized } = usePriceDetailsHelpers()

  const { nights, totalData, wallet, onWalletToggle, computed, display, totals } =
    usePriceDetailsContext()

  const {
    enrichedRooms,
    aggregatedData,
    groupedTaxDetails,
    paymentTotals,
    payAtPropertyCurrency,
    battlefaceProducts,
  } = computed

  const {
    displayInsuranceSection,
    displayWalletSection,
    displayBattlefaceSection,
    displayPayAtPropertySection,
    displayTaxDetailsButton,
  } = display

  const { totalPrice, totalInsurance, totalInsuranceCurrency } = totals

  // Early return if no valid data
  if (!aggregatedData || !nights || nights <= 0) {
    return null
  }

  const { totalCurrency, paymentDisplayType } = aggregatedData

  // Labels and texts
  const localCurrencyText =
    displayPayAtPropertySection && payAtPropertyCurrency
      ? `${formatMessage({ id: 'global.local-currency' })} ${formatMessage(
          { id: 'tax-breakdown.payment-at-property' },
          { currency: payAtPropertyCurrency }
        )}`
      : formatMessage({ id: 'global.local-currency' })

  const depositSuffix =
    paymentDisplayType === PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT
      ? ` (${formatMessage({ id: 'policies.deposit' })})`
      : ''

  const handleWalletChange = () => {
    if (wallet?.loading) return
    onWalletToggle?.(!wallet?.selected)
  }

  return (
    <>
      <div className={styles.section}>
        {enrichedRooms.map((room, index) => (
          <RoomSection key={`room-${index + 1}`} room={room} />
        ))}

        {(displayInsuranceSection || displayWalletSection || displayBattlefaceSection) && (
          <div className={styles.sections}>
            {displayBattlefaceSection &&
              battlefaceProducts.map(([key, value]) => (
                <div key={key} className={styles.insuranceRow}>
                  <Typography variant="body" size="l">
                    {formatMessage(
                      {
                        id: 'insurance-package.summary',
                      },
                      { insurance: formatMessage({ id: getBattlefaceLabel(key) }) }
                    )}
                  </Typography>
                  <Typography variant="body" size="l">
                    {formatCurrency(value, totalCurrency)}
                  </Typography>
                </div>
              ))}

            {displayInsuranceSection && (
              <div className={styles.insuranceRow}>
                <Typography variant="body" size="l">
                  {formatMessage({ id: 'insurance.refund-protect' })}
                </Typography>
                <Typography variant="body" size="l">
                  {formatCurrency(totalInsurance, totalInsuranceCurrency)}
                </Typography>
              </div>
            )}

            {displayWalletSection && (
              <div className={styles.insuranceRow}>
                <Checkbox
                  name="staywallet"
                  onChange={handleWalletChange}
                  selected={Boolean(wallet?.selected)}
                  disabled={wallet?.loading}
                >
                  <Typography variant="body" size="l">
                    {`${formatMessage({ id: 'serp.filter-apply' })} ${formatMessage({ id: 'staywallet.credit' })}`}
                  </Typography>
                </Checkbox>
                <Typography variant="body" size="l" bold color="var(--color-success-500)">
                  {`-${formatCurrency(wallet?.availableAmount, wallet?.currency)}`}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      <Separator className={styles.separator} />

      <div className={styles.sectionTotal}>
        <div className={styles.totalHeader}>
          <Typography variant="title" size="xs" bold>
            {formatMessage({ id: 'global.total' })}
          </Typography>
          <Typography variant="title" size="xs" bold>
            {formatCurrency(Math.round(totalPrice), totalCurrency)}
          </Typography>
        </div>

        <div className={styles.detailsBlockDark}>
          <PaymentDisplay
            totalData={totalData}
            aggregatedData={aggregatedData}
            totalPrice={totalPrice}
            depositSuffix={depositSuffix}
          />

          {displayPayAtPropertySection && (
            <PaymentRow
              label={formatMessageCapitalized({ id: 'tax-breakdown.pay-at-hotel' })}
              amount={Math.round(paymentTotals.payLater)}
              currency={paymentTotals.currency || totalCurrency}
              withTaxDetails={displayTaxDetailsButton}
              taxDetails={groupedTaxDetails}
            />
          )}
        </div>
      </div>

      <div className={styles.content}>
        <Typography variant="caption" size="l" bold>
          {`${formatMessage({ id: 'global.no-conversion-fees' })}`}
        </Typography>
        <Typography variant="caption" size="l">
          {` ${localCurrencyText}`}
        </Typography>
      </div>
    </>
  )
}

PriceDetailsContent.displayName = 'PriceDetailsContent'
